import { useEffect, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Modal } from "../components/Modal";
import { FeedbackContact } from "../feedback/FeedbackContact";
import { GettingStartedChecklist } from "../guide/GettingStartedChecklist";
import { GuideCard, GuideList } from "../guide/GuideCard";
import { CREATE_CLASS_ITEMS } from "../guide/guideSteps";
import { useGuideState } from "../guide/guideStorage";
import { classPath, useNavigate } from "../router";

import { inactiveStudents } from "../inactiveStudents";

import type { ApiErrorCode } from "../api/client";
import type { ClassRecord, StudentAccount } from "@canvaschool/contracts";

type InactiveTarget = { classId: string; className: string } & Pick<
  StudentAccount,
  "studentNumber" | "displayName"
>;

const todayKey = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const inactiveCheckKey = (userId: string) =>
  `canvaschool:inactiveCheck:${userId}`;

const ClassCards = ({
  classes,
  archived,
  onReactivate,
}: {
  classes: ClassRecord[];
  archived: boolean;
  onReactivate?: (item: ClassRecord) => void;
}) => (
  <ul className="canvaschool-class-list">
    {classes.map((item) => (
      <li key={item.id} className="canvaschool-class-row">
        <div className="canvaschool-class-info">
          <h3 className="canvaschool-class-name">{item.name}</h3>
          <p className="canvaschool-class-status">
            {archived
              ? "학생 로그인과 편집이 중지되었습니다. 읽기와 내보내기는 계속할 수 있습니다."
              : null}
            {!archived && (
              <span className="canvaschool-prefix">{item.accountPrefix}</span>
            )}
          </p>
        </div>
        {archived ? (
          <div className="canvaschool-actions">
            <Link className="canvaschool-btn-secondary" to={classPath(item.id)}>
              학급 보기
            </Link>
            {onReactivate && (
              <button type="button" onClick={() => onReactivate(item)}>
                다시 사용
              </button>
            )}
          </div>
        ) : (
          <div className="canvaschool-actions">
            <Link className="canvaschool-btn-primary" to={classPath(item.id)}>
              학급 열기
            </Link>
          </div>
        )}
      </li>
    ))}
  </ul>
);

export const ClassListPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<ClassRecord[] | null>(null);
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [studentCount, setStudentCount] = useState("30");
  const [studentCountError, setStudentCountError] = useState(false);
  const [pending, setPending] = useState(false);
  const [targets, setTargets] = useState<InactiveTarget[] | null>(null);
  const [targetsFailed, setTargetsFailed] = useState(false);
  const principal =
    auth.state.status === "authenticated" ? auth.state.session.principal : null;
  const teacherId = principal?.role === "TEACHER" ? principal.userId : null;
  const guide = useGuideState(principal?.userId ?? "");

  useEffect(() => {
    let cancelled = false;
    auth.api.listClasses("ALL").then(
      (page) => !cancelled && setClasses(page.items),
      (failure) => !cancelled && setError(auth.handleError(failure)),
    );
    return () => {
      cancelled = true;
    };
    // Load once per mount; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.api]);

  // Once a day, offer to stop students unused for two weeks.
  useEffect(() => {
    if (!teacherId || !classes) {
      return;
    }
    const today = todayKey();
    try {
      if (window.localStorage.getItem(inactiveCheckKey(teacherId)) === today) {
        return;
      }
    } catch {
      // Without storage the prompt may repeat; that is acceptable.
    }
    let cancelled = false;
    const now = new Date();
    void Promise.allSettled(
      classes
        .filter((item) => item.status === "ACTIVE")
        .map(async (item) =>
          inactiveStudents(item, await auth.api.getRoster(item.id), now).map(
            (student): InactiveTarget => ({
              classId: item.id,
              className: item.name,
              studentNumber: student.studentNumber,
              displayName: student.displayName,
            }),
          ),
        ),
    ).then((results) => {
      if (cancelled) {
        return;
      }
      // A failed roster fetch skips the date key so the next visit retries.
      if (results.every((result) => result.status === "fulfilled")) {
        try {
          window.localStorage.setItem(inactiveCheckKey(teacherId), today);
        } catch {
          // Asked again on the next visit.
        }
      }
      const found = results.flatMap((result) =>
        result.status === "fulfilled" ? result.value : [],
      );
      if (found.length > 0) {
        setTargets(found);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, classes]);

  const endNumber = Math.min(
    30,
    Math.max(1, Math.trunc(Number(studentCount)) || 1),
  );

  const deactivateTargets = async () => {
    if (!targets) {
      return;
    }
    setPending(true);
    setTargetsFailed(false);
    const failed: InactiveTarget[] = [];
    for (const target of targets) {
      try {
        await auth.api.updateStudent(target.classId, target.studentNumber, {
          enabled: false,
        });
      } catch {
        failed.push(target);
      }
    }
    setPending(false);
    setTargets(failed.length > 0 ? failed : null);
    setTargetsFailed(failed.length > 0);
  };

  const active = classes?.filter((item) => item.status === "ACTIVE") ?? [];
  const archived = classes?.filter((item) => item.status === "ARCHIVED") ?? [];
  const empty = classes !== null && classes.length === 0;

  const openCreate = () => {
    setName("");
    setPrefix("");
    setStudentCount("30");
    setStudentCountError(false);
    setCreating(true);
  };

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>내 학급</h1>
          <p className="canvaschool-page-lead">
            {classes === null
              ? ""
              : empty
              ? "아직 학급이 없습니다."
              : `진행 중 ${active.length}개 · 보관됨 ${archived.length}개`}
          </p>
        </div>
        <button
          type="button"
          className="canvaschool-btn-primary"
          onClick={openCreate}
        >
          학급 만들기
        </button>
      </div>
      {error && <ErrorAlert code={error} />}
      {teacherId && classes && (
        <GettingStartedChecklist
          guide={guide}
          mode={empty ? "empty" : "list"}
          done={{
            class: !empty,
            share: null,
            login: null,
            canvas: null,
            lesson: guide.state.openedCanvas,
          }}
          onShortcut={() => openCreate()}
        />
      )}
      {empty && (!teacherId || guide.state.hidden) && (
        <div className="canvaschool-empty">
          <p className="canvaschool-empty-title">첫 학급을 만들어 보세요.</p>
          <p className="canvaschool-empty-text">
            학급을 만들면 정한 번호까지(최대 30개) 학생 계정이 준비됩니다.
          </p>
          <button
            type="button"
            className="canvaschool-btn-primary"
            onClick={openCreate}
          >
            학급 만들기
          </button>
        </div>
      )}
      {classes && !empty && (
        <>
          <h2 className="canvaschool-group-title">진행 중</h2>
          <ClassCards classes={active} archived={false} />
          {archived.length > 0 && (
            <>
              <h2 className="canvaschool-group-title">보관됨</h2>
              <ClassCards
                classes={archived}
                archived
                onReactivate={(item) => {
                  setPending(true);
                  setError(null);
                  void auth.api.reactivateClass(item.id).then(
                    async () => {
                      const page = await auth.api.listClasses("ALL");
                      setClasses(page.items);
                      setPending(false);
                    },
                    (failure) => {
                      setError(auth.handleError(failure));
                      setPending(false);
                    },
                  );
                }}
              />
            </>
          )}
        </>
      )}
      <FeedbackContact api={auth.api} />
      {creating && (
        <Modal
          title="학급 만들기"
          formName="학급 만들기"
          primaryLabel="학급 만들기"
          pending={pending}
          onCancel={() => setCreating(false)}
          onPrimary={() => {
            const count = Number(studentCount);
            if (!Number.isInteger(count) || count < 1 || count > 30) {
              setStudentCountError(true);
              return;
            }
            setStudentCountError(false);
            setPending(true);
            setError(null);
            void auth.api
              .createClass(name, prefix, count)
              .then(async (created) => {
                // Every new class starts with the sample canvas. The class
                // already exists, so a failure here is swallowed, not shown.
                try {
                  // Loaded on demand: keeps Excalidraw's transform and font
                  // code out of the class list bundle.
                  const { buildSampleScene, SAMPLE_CANVAS_TITLE } =
                    await import("../editor/sampleCanvas");
                  const scene = await buildSampleScene();
                  await auth.api.createCanvas(
                    created.class.id,
                    SAMPLE_CANVAS_TITLE,
                    { template: "GROUP", scene },
                  );
                } catch (sampleFailure) {
                  console.warn("sample canvas was not created", sampleFailure);
                }
                return created;
              })
              .then(
                (created) => navigate(classPath(created.class.id)),
                (failure) => {
                  setError(auth.handleError(failure));
                  setPending(false);
                },
              );
          }}
        >
          {teacherId && (
            <GuideCard
              id="create-class"
              title="학급을 만들면"
              userId={teacherId}
              dismissible={false}
            >
              <GuideList items={CREATE_CLASS_ITEMS} />
            </GuideCard>
          )}
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-class-name"
              className="canvaschool-form-label"
            >
              학급 이름
            </label>
            <input
              id="canvaschool-class-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="예: 3학년 1반, 5학년 미술동아리"
              required
              autoFocus
            />
          </div>
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-class-prefix"
              className="canvaschool-form-label"
            >
              계정 접두어
            </label>
            <input
              id="canvaschool-class-prefix"
              aria-describedby="canvaschool-class-prefix-hint"
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
              placeholder="예: class3-1"
              required
            />
            <p id="canvaschool-class-prefix-hint" className="canvaschool-hint">
              영문 소문자로 시작, 소문자·숫자·- 만 써서 3~20자
              <br />
              {prefix.trim() ? "" : "예) "}
              <code>{prefix.trim() || "class3-1"}</code> → 학생 계정{" "}
              <code>{prefix.trim() || "class3-1"}_1</code> ~{" "}
              <code>
                {prefix.trim() || "class3-1"}_{endNumber}
              </code>
            </p>
          </div>
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-class-student-count"
              className="canvaschool-form-label"
            >
              학생 마지막 번호
            </label>
            <input
              id="canvaschool-class-student-count"
              type="number"
              min={1}
              max={30}
              step={1}
              value={studentCount}
              onChange={(event) => setStudentCount(event.target.value)}
              aria-invalid={studentCountError}
              required
            />
            {studentCountError && (
              <p role="alert" className="canvaschool-hint">
                1부터 30 사이의 정수를 입력해 주세요.
              </p>
            )}
          </div>
        </Modal>
      )}
      {targets && (
        <Modal
          title="2주 동안 이용이 없는 학생"
          primaryLabel="사용 중지"
          secondaryLabel="아니요"
          danger
          pending={pending}
          onCancel={() => !pending && setTargets(null)}
          onPrimary={() => void deactivateTargets()}
        >
          <ul>
            {targets.map((target) => (
              <li key={`${target.classId}-${target.studentNumber}`}>
                {target.className} · {target.studentNumber}번
                {target.displayName ? ` ${target.displayName}` : ""}
              </li>
            ))}
          </ul>
          {targetsFailed && (
            <p role="alert">
              일부 학생을 중지하지 못했습니다. 다시 시도해 주세요.
            </p>
          )}
          <p>이 학생들을 사용 중지할까요?</p>
        </Modal>
      )}
    </section>
  );
};
